// Login + Session-Handling (httpOnly-Cookies)
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Loader2, LogIn } from "lucide-react";
import { SunworkerLogo } from "./SunworkerLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

let refreshing = null;
axios.interceptors.response.use(undefined, async (err) => {
  const cfg = err.config || {};
  if (err.response?.status === 401 && !cfg._retry && !String(cfg.url).includes("/auth/")) {
    cfg._retry = true;
    try {
      refreshing = refreshing || axios.post(`${API}/auth/refresh`);
      await refreshing;
      refreshing = null;
      return axios(cfg);
    } catch (e) {
      refreshing = null;
      window.dispatchEvent(new Event("sw-logout"));
    }
  }
  return Promise.reject(err);
});

export function formatApiError(detail) {
  if (detail == null) return "Etwas ist schiefgelaufen. Bitte erneut versuchen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return detail.msg || String(detail);
}

const startGoogleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export function AuthGate({ children }) {
  const [user, setUser] = useState(null);
  const [googleMsg, setGoogleMsg] = useState("");
  const handled = useRef(false);
  useEffect(() => {
    const out = () => setUser(false);
    window.addEventListener("sw-logout", out);
    if (!handled.current) {
      handled.current = true;
      const m = window.location.hash.match(/session_id=([^&]+)/);
      if (m) {
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
        axios.post(`${API}/auth/google`, { session_id: decodeURIComponent(m[1]) })
          .then((r) => setUser(r.data))
          .catch((e) => { setGoogleMsg(formatApiError(e.response?.data?.detail)); setUser(false); });
      } else {
        axios.get(`${API}/auth/me`).then((r) => setUser(r.data)).catch(() =>
          axios.post(`${API}/auth/refresh`).then((r) => setUser(r.data)).catch(() => setUser(false)));
      }
    }
    return () => window.removeEventListener("sw-logout", out);
  }, []);
  if (user === null) return <div className="min-h-screen eb-bg flex items-center justify-center"><Loader2 className="animate-spin text-eb-primary" /></div>;
  if (!user) return <LoginPage onLogin={setUser} googleMsg={googleMsg} />;
  return children(user, async () => { await axios.post(`${API}/auth/logout`); setUser(false); });
}

function LoginPage({ onLogin, googleMsg }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { onLogin((await axios.post(`${API}/auth/login`, { email, password })).data); }
    catch (e2) { setErr(formatApiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen eb-bg grain flex items-center justify-center p-6" data-testid="login-page">
      <form onSubmit={submit} className="card w-full max-w-sm space-y-4 relative z-10">
        <div className="flex items-center gap-3">
          <SunworkerLogo size={40} />
          <div><div className="font-heading font-bold text-lg">Sunworker</div><div className="text-xs text-eb-muted">KI &amp; IT Management · Anmeldung</div></div>
        </div>
        <input type="email" placeholder="E-Mail" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
        <input type="password" placeholder="Passwort" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
        {err && <div className="text-rose-400 text-xs" data-testid="login-error">{err}</div>}
        <button className="btn btn-primary w-full justify-center" disabled={busy} data-testid="login-submit">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <LogIn size={14} />} Anmelden
        </button>
        <div className="flex items-center gap-3 text-[11px] text-eb-muted"><span className="flex-1 h-px bg-white/10" />oder<span className="flex-1 h-px bg-white/10" /></div>
        <button type="button" onClick={startGoogleLogin} className="btn w-full justify-center" data-testid="login-google">
          <svg width="14" height="14" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
          Mit Google anmelden
        </button>
        {googleMsg && <div className="text-amber-300 text-xs" data-testid="login-google-message">{googleMsg}</div>}
      </form>
    </div>
  );
}
